import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { WidgetInfo } from '@/lib/widget-info';
import { DrillPanel } from './DrillPanel';

interface InfoButtonProps {
  /** Navnet på widgeten eller tallet, brukt i aria-label og som paneltittel når `info.title` mangler. */
  name: string;
  info: WidgetInfo;
  size?: 'md' | 'sm';
  className?: string;
}

/** (i)-knapp som åpner «Om tallene» i det felles drill-panelet (VIB-2990). */
export function InfoButton({ name, info, size = 'md', className }: InfoButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label={`Om tallene i ${name}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={cn(
          'inline-flex items-center justify-center shrink-0 rounded-full border-[1.5px] border-foreground/35',
          'font-display font-semibold italic leading-none text-[#4A5A6A]',
          'hover:border-primary hover:text-primary hover:bg-primary/[0.06]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          size === 'md' ? 'w-5 h-5 text-[12px]' : 'w-4 h-4 text-[10px]',
          className,
        )}
      >
        i
      </button>
      <DrillPanel isOpen={open} onClose={() => setOpen(false)} kicker="Om tallene" title={info.title ?? name}>
        <div className="px-4 sm:px-6 py-5">
          <p className="text-[15px] text-foreground leading-normal">{info.summary}</p>
          <p className="mt-[22px] pb-1.5 border-b border-foreground/[0.12] text-[11px] uppercase tracking-[0.06em] text-[#8A97A3] font-medium">
            Slik regnes det
          </p>
          <dl>
            {info.terms.map((t) => (
              <div key={t.term} className="py-2.5 border-b border-foreground/[0.06]">
                <dt className="font-semibold text-[14px]">{t.term}</dt>
                <dd className="text-[13.5px] text-[#4A5A6A] leading-normal mt-0.5">{t.text}</dd>
              </div>
            ))}
          </dl>
          {info.note && (
            <p className="mt-4 px-3.5 py-3 rounded-[10px] bg-muted text-[13.5px] text-[#4A5A6A] leading-normal">{info.note}</p>
          )}
        </div>
      </DrillPanel>
    </>
  );
}
